import { DomOverlay } from './DomOverlay';
import { Haptics } from '../Haptics';
import { Family, FAMILY_COLORS, FAMILY_LABELS, MAX_ACTIVE_FAMILIES_PER_RUN } from '../../config';

type DraftEvents = {
  /** User confirmed their draft. Payload is the chosen families (1..MAX). */
  confirm: Family[];
  /** User dismissed the overlay without drafting (not currently exposed). */
  cancel: void;
};

interface DraftCardInfo {
  family: Family;
  shape: string;       // Shape name — "Triangle", "Teardrop", etc.
  glyph: string;       // SVG glyph inner content
  tagline: string;     // 2–3 word descriptor
}

const CARDS: DraftCardInfo[] = [
  { family: Family.Fire,      shape: 'Triangle', glyph: '<polygon points="60,18 104,94 16,94"/>',            tagline: 'Combustion • Impulse' },
  { family: Family.Water,     shape: 'Teardrop', glyph: '<path d="M60,18 C84,50 96,70 96,80 A36 32 0 1 1 24,80 C24,70 36,50 60,18 Z"/>', tagline: 'Flow • Precision' },
  { family: Family.Earth,     shape: 'Hexagon',  glyph: '<polygon points="60,14 106,40 106,86 60,112 14,86 14,40"/>', tagline: 'Mass • Defense' },
  { family: Family.Air,       shape: 'Swirl',    glyph: '<path d="M18,70 Q60,10 102,70 Q60,60 18,70 Z" opacity="0.85"/><path d="M18,86 Q60,40 102,86" fill="none" stroke="currentColor" stroke-width="6" stroke-linecap="round"/>', tagline: 'Mobility • Reach' },
  { family: Family.Lightning, shape: 'Bolt',     glyph: '<polygon points="66,10 30,66 60,66 48,114 96,52 62,52 82,10"/>', tagline: 'Speed • Chains' },
  { family: Family.Shadow,    shape: 'Crescent', glyph: '<path d="M60,14 A46,46 0 1 0 92,100 A36,36 0 1 1 60,14 Z"/>',  tagline: 'Stealth • Control' },
  { family: Family.Light,     shape: 'Star',     glyph: '<polygon points="60,8 74,52 118,60 74,68 60,112 46,68 2,60 46,52"/>', tagline: 'Burst • Clarity' },
  { family: Family.Void,      shape: 'Octagon',  glyph: '<polygon points="40,10 80,10 110,40 110,80 80,110 40,110 10,80 10,40"/>', tagline: 'Entropy • Reach' },
];

/**
 * DraftOverlay — full-screen card carousel for the pre-run family draft.
 *
 * Designer brief §2 / Part 1 item #2: shift from a static list to a Marvel-
 * Snap-style card carousel. Swipe left/right to change the focused card,
 * tap the card to toggle selection. A pinned selection counter at the top
 * shows `n / MAX`. "Begin Run" at the bottom is disabled until ≥ 1 selected.
 *
 * Only unlocked families are offered. `Wild` is excluded (always available,
 * never drafted). Pre-selected by default to whatever was active last run.
 */
export class DraftOverlay extends DomOverlay<DraftEvents> {
  private cards: DraftCardInfo[];
  private selected: Set<Family>;
  private focusIndex = 0;

  private trackEl!: HTMLDivElement;
  private counterEl!: HTMLDivElement;
  private confirmBtnEl!: HTMLButtonElement;

  private dragStartX = 0;
  private dragDelta = 0;
  private dragging = false;
  private cardWidth = 260;

  constructor(options: { unlocked: Family[]; preselected: Family[] }) {
    super();
    // Widen the Set's element type so filter() below isn't narrowed to the
    // specific literal union that each CARD's family resolves to.
    const unlockedSet: Set<Family> = new Set<Family>(
      options.unlocked.filter((f) => f !== Family.Wild)
    );
    this.cards = CARDS.filter((c) => unlockedSet.has(c.family));
    this.selected = new Set<Family>(
      options.preselected.filter((f) => unlockedSet.has(f))
    );
    if (this.cards.length === 0) {
      // Defensive: should never happen — draft shouldn't open without unlocks.
      this.cards = [CARDS[0]];
    }
  }

  protected render(): void {
    this.rootEl.innerHTML = '';
    this.rootEl.appendChild(this.buildContent());

    if (!document.getElementById('draft-overlay-styles')) {
      const style = this.el('style');
      style.id = 'draft-overlay-styles';
      style.textContent = DRAFT_STYLES;
      document.head.appendChild(style);
    }
  }

  private buildContent(): HTMLDivElement {
    const wrap = this.el('div', 'draft-wrap');

    // Header
    const header = this.el('div', 'draft-header');
    header.appendChild(this.el('div', 'type-heading draft-eyebrow', 'PRE-RUN DRAFT'));
    header.appendChild(this.el('h1', 'type-display draft-title', 'Choose Your Families'));
    header.appendChild(this.el('p', 'type-body draft-hint', `Swipe through the available families and tap to select up to ${MAX_ACTIVE_FAMILIES_PER_RUN}.`));
    wrap.appendChild(header);

    // Selection counter
    this.counterEl = this.el('div', 'draft-counter type-numeric');
    wrap.appendChild(this.counterEl);

    // Carousel
    const carousel = this.el('div', 'draft-carousel');
    this.trackEl = this.el('div', 'draft-track');
    for (let i = 0; i < this.cards.length; i++) {
      this.trackEl.appendChild(this.buildCard(this.cards[i], i));
    }
    carousel.appendChild(this.trackEl);
    wrap.appendChild(carousel);

    // Dots indicator
    const dots = this.el('div', 'draft-dots');
    for (let i = 0; i < this.cards.length; i++) {
      const dot = this.el('button', 'draft-dot');
      dot.dataset.index = String(i);
      dot.addEventListener('click', () => {
        Haptics.selection();
        this.focusIndex = i;
        this.layoutTrack(true);
      });
      dots.appendChild(dot);
    }
    wrap.appendChild(dots);

    // Footer — cancel (ghost) + begin run (filled)
    const footer = this.el('div', 'draft-footer');
    this.confirmBtnEl = this.el('button', 'btn btn--filled draft-confirm', 'Begin Run');
    this.confirmBtnEl.addEventListener('click', () => {
      if (this.selected.size === 0) return;
      Haptics.heavy();
      this.emit('confirm', [...this.selected]);
    });
    footer.appendChild(this.confirmBtnEl);
    wrap.appendChild(footer);

    // Pointer drag for swipe navigation
    carousel.addEventListener('pointerdown', this.onPointerDown);
    carousel.addEventListener('pointermove', this.onPointerMove);
    carousel.addEventListener('pointerup', this.onPointerUp);
    carousel.addEventListener('pointercancel', this.onPointerUp);
    carousel.addEventListener('pointerleave', this.onPointerUp);

    // Initial layout on next frame so we have an accurate cardWidth.
    requestAnimationFrame(() => {
      const firstCard = this.trackEl.firstElementChild as HTMLElement | null;
      if (firstCard) this.cardWidth = firstCard.offsetWidth + 20; // width + gap
      this.layoutTrack(false);
      this.refreshCounter();
      this.refreshConfirm();
    });

    return wrap;
  }

  private buildCard(info: DraftCardInfo, index: number): HTMLDivElement {
    const hex = `#${FAMILY_COLORS[info.family].toString(16).padStart(6, '0')}`;
    const card = this.el('div', 'draft-card glass-panel');
    card.dataset.index = String(index);
    card.style.setProperty('--card-color', hex);

    card.innerHTML = `
      <div class="draft-card__glow"></div>
      <div class="draft-card__shape-wrap">
        <svg class="draft-card__shape" viewBox="0 0 120 120" aria-hidden="true">
          <g fill="${hex}" stroke="${hex}" stroke-width="2" stroke-linejoin="round">
            ${info.glyph}
          </g>
        </svg>
      </div>
      <div class="draft-card__text">
        <div class="draft-card__label type-heading">${info.shape}</div>
        <div class="draft-card__name type-display">${FAMILY_LABELS[info.family]}</div>
        <div class="draft-card__tagline type-body">${info.tagline}</div>
      </div>
      <div class="draft-card__status">
        <span class="draft-card__status-label">Tap to ${this.selected.has(info.family) ? 'remove' : 'draft'}</span>
      </div>
    `;

    card.addEventListener('click', (e) => {
      // Ignore clicks that originated from a drag.
      if (Math.abs(this.dragDelta) > 6) return;
      e.stopPropagation();
      this.toggleSelection(info.family);
    });

    return card;
  }

  private toggleSelection(family: Family): void {
    if (this.selected.has(family)) {
      this.selected.delete(family);
      Haptics.light();
    } else {
      if (this.selected.size >= MAX_ACTIVE_FAMILIES_PER_RUN) {
        // Replace the oldest selection — feels more forgiving than silent reject.
        const first = this.selected.values().next().value as Family | undefined;
        if (first !== undefined) this.selected.delete(first);
      }
      this.selected.add(family);
      Haptics.medium();
    }
    this.refreshCounter();
    this.refreshConfirm();
    this.refreshCardStates();
  }

  private refreshCounter(): void {
    this.counterEl.textContent = `${this.selected.size} / ${MAX_ACTIVE_FAMILIES_PER_RUN} drafted`;
    this.counterEl.classList.toggle('is-full', this.selected.size === MAX_ACTIVE_FAMILIES_PER_RUN);
  }

  private refreshConfirm(): void {
    this.confirmBtnEl.disabled = this.selected.size === 0;
    this.confirmBtnEl.classList.toggle('is-disabled', this.selected.size === 0);
  }

  private refreshCardStates(): void {
    const cards = this.trackEl.querySelectorAll<HTMLDivElement>('.draft-card');
    cards.forEach((card, i) => {
      const family = this.cards[i].family;
      const isSelected = this.selected.has(family);
      card.classList.toggle('is-selected', isSelected);
      const statusLabel = card.querySelector('.draft-card__status-label');
      if (statusLabel) statusLabel.textContent = `Tap to ${isSelected ? 'remove' : 'draft'}`;
    });
  }

  private layoutTrack(spring: boolean): void {
    const N = this.cards.length;
    // Clamp focus index
    this.focusIndex = Math.max(0, Math.min(N - 1, this.focusIndex));

    // Center the focused card
    const offset = -this.focusIndex * this.cardWidth + (this.dragging ? this.dragDelta : 0);
    this.trackEl.style.transition = spring
      ? 'transform 520ms var(--ease-spring)'
      : 'none';
    this.trackEl.style.transform = `translate3d(${offset}px, 0, 0)`;

    // Scale/tilt cards based on distance from focus
    const cards = this.trackEl.querySelectorAll<HTMLDivElement>('.draft-card');
    cards.forEach((card, i) => {
      const d = i - this.focusIndex;
      const scale = Math.max(0.82, 1 - Math.abs(d) * 0.08);
      const rotY = d * -6;
      const translateX = d * 6;
      card.style.transform = `translateX(${translateX}px) scale(${scale}) rotateY(${rotY}deg)`;
      card.style.opacity = String(Math.max(0.45, 1 - Math.abs(d) * 0.28));
      card.classList.toggle('is-focused', d === 0);
    });

    // Dots
    const dots = this.rootEl.querySelectorAll<HTMLButtonElement>('.draft-dot');
    dots.forEach((dot, i) => dot.classList.toggle('is-active', i === this.focusIndex));

    this.refreshCardStates();
  }

  // Drag handling — bind `this` so we can add/remove these cleanly.
  private onPointerDown = (e: PointerEvent) => {
    this.dragging = true;
    this.dragStartX = e.clientX;
    this.dragDelta = 0;
    (e.currentTarget as Element).setPointerCapture(e.pointerId);
  };

  private onPointerMove = (e: PointerEvent) => {
    if (!this.dragging) return;
    this.dragDelta = e.clientX - this.dragStartX;
    this.layoutTrack(false);
  };

  private onPointerUp = (e: PointerEvent) => {
    if (!this.dragging) return;
    this.dragging = false;

    // Commit: if drag exceeded 25% of a card width, advance focus
    const threshold = this.cardWidth * 0.25;
    if (this.dragDelta < -threshold && this.focusIndex < this.cards.length - 1) {
      this.focusIndex++;
      Haptics.selection();
    } else if (this.dragDelta > threshold && this.focusIndex > 0) {
      this.focusIndex--;
      Haptics.selection();
    }
    this.dragDelta = 0;
    this.layoutTrack(true);

    if ((e.currentTarget as Element).hasPointerCapture?.(e.pointerId)) {
      (e.currentTarget as Element).releasePointerCapture(e.pointerId);
    }
  };
}

// ============================================================================
// Scoped styles for the draft carousel
// ============================================================================

const DRAFT_STYLES = `
.draft-wrap {
  width: 100%;
  max-width: 420px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 18px;
}
.draft-header {
  text-align: center;
  animation: spring-in 560ms var(--ease-spring) both;
}
.draft-eyebrow {
  margin: 0 0 6px;
}
.draft-title {
  font-size: 28px;
  margin: 0 0 6px;
  letter-spacing: 0.01em;
}
.draft-hint {
  margin: 0 auto;
  max-width: 300px;
  font-size: 12px;
  color: var(--text-tertiary);
}
.draft-counter {
  font-size: 13px;
  padding: 8px 18px;
  border-radius: 999px;
  background: rgba(13, 14, 20, 0.7);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  border: 1px solid rgba(0, 240, 255, 0.25);
  color: var(--accent-cyan);
  letter-spacing: 0.15em;
  transition: border-color 300ms var(--ease-snap), color 300ms var(--ease-snap);
}
.draft-counter.is-full {
  border-color: rgba(0, 240, 255, 0.85);
  color: var(--text-primary);
  box-shadow: 0 0 32px -4px rgba(0, 240, 255, 0.65);
}
.draft-carousel {
  width: 100%;
  height: 360px;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  touch-action: none;
  perspective: 1100px;
}
.draft-track {
  position: absolute;
  top: 0; bottom: 0;
  left: 50%;
  display: flex;
  align-items: center;
  gap: 20px;
  will-change: transform;
}
.draft-card {
  flex: 0 0 240px;
  height: 340px;
  margin-left: -120px; /* center each card over the track's left:50% origin */
  padding: 24px 22px;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 12px;
  cursor: pointer;
  transition:
    transform 520ms var(--ease-spring),
    opacity 520ms var(--ease-spring),
    border-color 320ms var(--ease-snap),
    box-shadow 320ms var(--ease-snap);
  position: relative;
  overflow: hidden;
  transform-origin: center center;
  will-change: transform, opacity;
}
.draft-card__glow {
  position: absolute;
  inset: -1px;
  pointer-events: none;
  border-radius: 18px;
  background: radial-gradient(circle at 50% 0%, var(--card-color, #00F0FF) 0%, transparent 60%);
  opacity: 0.08;
  transition: opacity 300ms;
}
.draft-card.is-focused .draft-card__glow { opacity: 0.22; }
.draft-card.is-selected {
  border-color: var(--card-color, #00F0FF) !important;
  box-shadow:
    0 0 0 1px var(--card-color, #00F0FF) inset,
    0 18px 60px -8px color-mix(in srgb, var(--card-color, #00F0FF) 55%, transparent),
    0 0 80px -10px color-mix(in srgb, var(--card-color, #00F0FF) 65%, transparent);
}
.draft-card.is-selected .draft-card__glow { opacity: 0.5; }
.draft-card__shape-wrap {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px;
  border-radius: 14px;
  background: rgba(5, 5, 7, 0.45);
  border: 1px solid rgba(255, 255, 255, 0.04);
}
.draft-card__shape {
  width: 150px;
  height: 150px;
  filter: drop-shadow(0 0 18px color-mix(in srgb, var(--card-color, #00F0FF) 60%, transparent));
  color: var(--card-color, #00F0FF);
}
.draft-card__text { text-align: left; }
.draft-card__label { color: var(--card-color, #00F0FF); font-size: 10px; margin-bottom: 4px; }
.draft-card__name  { font-size: 22px; margin: 0 0 4px; letter-spacing: 0.02em; }
.draft-card__tagline { font-size: 11px; color: var(--text-tertiary); margin: 0; letter-spacing: 0.1em; text-transform: uppercase; }
.draft-card__status {
  position: absolute;
  bottom: 14px;
  left: 22px;
  right: 22px;
  text-align: center;
  font-family: var(--font-body);
  font-size: 10px;
  color: var(--text-tertiary);
  letter-spacing: 0.18em;
  text-transform: uppercase;
}
.draft-card.is-selected .draft-card__status-label { color: var(--card-color, #00F0FF); }

.draft-dots {
  display: flex;
  gap: 0;
  margin-top: 2px;
}
/* 44×44 hit target for accessibility (WCAG 2.5.5 / VISUAL-IDENTITY.md §12.3);
   the visual dot is rendered via ::after so the layout stays tight. */
.draft-dot {
  width: 44px;
  height: 44px;
  border-radius: 999px;
  background: transparent;
  border: none;
  padding: 0;
  cursor: pointer;
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.draft-dot::after {
  content: "";
  width: 8px;
  height: 8px;
  border-radius: 999px;
  background: rgba(136, 146, 176, 0.25);
  transition: background 300ms var(--ease-snap), transform 300ms var(--ease-spring), box-shadow 300ms var(--ease-snap);
}
.draft-dot.is-active::after {
  background: var(--accent-cyan);
  transform: scale(1.25);
  box-shadow: 0 0 16px -2px rgba(0, 240, 255, 0.8);
}

.draft-footer {
  width: 100%;
  display: flex;
  justify-content: center;
  padding-top: 8px;
}
.draft-confirm {
  min-width: 220px;
}
.draft-confirm.is-disabled,
.draft-confirm[disabled] {
  opacity: 0.45;
  pointer-events: none;
  filter: saturate(0.3);
}
`;
